/**
 * Full-viewport ambient background. All motion uses transform/opacity only (no filter: hue-rotate
 * on large subtrees — avoids Chrome rectangular compositing / purple “tiles”).
 */
export function SiteAmbientBackground() {
  return (
    <div
      className="pointer-events-none fixed inset-0 z-0 min-h-0 w-full min-w-0 max-w-[100vw] overflow-hidden [isolation:isolate] [backface-visibility:hidden]"
      aria-hidden
    >
      {/* Foundation */}
      <div className="absolute inset-0 bg-base" />

      {/* Clipped stage so soft shapes never show sharp rectangular edges at viewport bounds */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Primary aurora — full-bleed conic, centered via keyframes only */}
        <div
          className="absolute left-1/2 top-1/2 h-[220vmin] w-[220vmin] motion-safe:animate-ambient-sheen motion-reduce:animate-none"
          style={{
            background: `conic-gradient(
              from 210deg at 50% 50%,
              rgba(15, 23, 42, 0) 0deg,
              rgba(31, 162, 199, 0.22) 50deg,
              rgba(99, 102, 241, 0.28) 120deg,
              rgba(192, 132, 252, 0.2) 200deg,
              rgba(244, 63, 94, 0.12) 280deg,
              rgba(15, 23, 42, 0) 360deg
            )`,
            opacity: 0.75,
          }}
        />

        {/* Counter-rotating wash */}
        <div
          className="absolute left-1/2 top-1/2 h-[190vmin] w-[190vmin] motion-safe:animate-ambient-sheen-reverse motion-reduce:animate-none"
          style={{
            background: `conic-gradient(
              from 0deg at 50% 50%,
              rgba(0, 0, 0, 0) 0deg,
              rgba(45, 212, 191, 0.15) 90deg,
              rgba(56, 189, 248, 0.12) 180deg,
              rgba(167, 139, 250, 0.18) 270deg,
              rgba(0, 0, 0, 0) 360deg
            )`,
            opacity: 0.55,
          }}
        />

        {/* Large blurred orbs — oversized & blurred so they merge smoothly */}
        <div
          className="absolute -left-1/4 -top-1/4 h-[90vmin] w-[90vmin] rounded-full blur-3xl motion-safe:animate-ambient-float-a motion-reduce:animate-none"
          style={{
            background: 'radial-gradient(circle at 40% 40%, rgba(99, 102, 241, 0.55), rgba(14, 165, 233, 0.2) 50%, transparent 70%)',
          }}
        />
        <div
          className="absolute -bottom-1/4 -right-1/4 h-[85vmin] w-[85vmin] rounded-full blur-3xl motion-safe:animate-ambient-float-b motion-reduce:animate-none"
          style={{
            background: 'radial-gradient(circle at 60% 55%, rgba(192, 132, 252, 0.45), rgba(244, 114, 182, 0.2) 55%, transparent 72%)',
          }}
        />
        <div
          className="absolute left-1/2 top-1/2 h-[70vmin] w-[70vmin] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl motion-safe:animate-ambient-float-c motion-reduce:animate-none"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(45, 212, 191, 0.35), rgba(59, 130, 246, 0.2) 60%, transparent 75%)',
          }}
        />
      </div>

      {/* Grid — full viewport, no mask (masks + animated opacity = seam bugs in some browsers) */}
      <div
        className="absolute inset-0 opacity-[0.2]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(199, 210, 254, 0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(165, 243, 252, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px',
        }}
      />

      {/* Soft edge darkening only — keeps content readable; must stay subtle or it kills colour */}
      <div className="absolute inset-0 bg-gradient-to-b from-base/[0.07] via-transparent to-base/20" />
    </div>
  );
}
